import { useRef, useState, type PointerEvent as RPointerEvent, type KeyboardEvent as RKeyboardEvent, type RefObject } from 'react';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import {
  LOGO_MAX_W,
  LOGO_MIN_W,
  QR_MAX_SIZE,
  QR_MIN_SIZE,
  type WallpaperLayout,
} from '../lib/qrWallpaper.js';

type ElementId = 'logo' | 'qr';

interface Props {
  canvasRef: RefObject<HTMLCanvasElement>;
  width: number;
  height: number;
  layout: WallpaperLayout;
  onChange: (layout: WallpaperLayout) => void;
  /** Natural height/width of the logo, or null without a logo. */
  logoRatio: number | null;
  showTemplate: boolean;
}

const SNAP = 0.015;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Wallpaper canvas with draggable/resizable logo and QR boxes laid over it (pointer, pinch and keyboard). */
export default function WallpaperPreview({ canvasRef, width, height, layout, onChange, logoRatio, showTemplate }: Props) {
  const { t } = useAppTranslation();
  const wrapRef = useRef<HTMLDivElement>(null);
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const [selected, setSelected] = useState<ElementId | null>(null);
  const [guides, setGuides] = useState({ x: false, y: false });
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const drag = useRef<{ id: ElementId; fx: number; fy: number; x: number; y: number } | null>(null);
  const pinch = useRef<{ id: ElementId; dist: number; size: number } | null>(null);
  const resizing = useRef<ElementId | null>(null);

  /** Box size as fractions of the wallpaper. */
  function box(id: ElementId, l: WallpaperLayout = layout) {
    if (id === 'qr') return { w: l.qr.size, h: (l.qr.size * width) / height };
    const w = l.logo.w;
    return { w, h: (w * width * (logoRatio ?? 0.3)) / height };
  }

  function sizeOf(id: ElementId, l: WallpaperLayout) { return id === 'qr' ? l.qr.size : l.logo.w; }
  const limits = (id: ElementId) => (id === 'qr' ? [QR_MIN_SIZE, QR_MAX_SIZE] as const : [LOGO_MIN_W, LOGO_MAX_W] as const);

  function patch(id: ElementId, p: Partial<{ x: number; y: number; size: number }>) {
    const l = layoutRef.current;
    if (id === 'qr') onChange({ ...l, qr: { ...l.qr, ...(p.x !== undefined && { x: p.x }), ...(p.y !== undefined && { y: p.y }), ...(p.size !== undefined && { size: p.size }) } });
    else onChange({ ...l, logo: { ...l.logo, ...(p.x !== undefined && { x: p.x }), ...(p.y !== undefined && { y: p.y }), ...(p.size !== undefined && { w: p.size }) } });
  }

  function frac(e: { clientX: number; clientY: number }) {
    const r = wrapRef.current!.getBoundingClientRect();
    return { fx: (e.clientX - r.left) / r.width, fy: (e.clientY - r.top) / r.height };
  }

  function moveTo(id: ElementId, x: number, y: number) {
    const b = box(id, layoutRef.current);
    let nx = clamp(x, Math.min(b.w / 2, 0.5), Math.max(1 - b.w / 2, 0.5));
    let ny = clamp(y, Math.min(b.h / 2, 0.5), Math.max(1 - b.h / 2, 0.5));
    const gx = Math.abs(nx - 0.5) < SNAP;
    const gy = Math.abs(ny - 0.5) < SNAP;
    if (gx) nx = 0.5;
    if (gy) ny = 0.5;
    setGuides({ x: gx, y: gy });
    patch(id, { x: nx, y: ny });
  }

  function onDown(e: RPointerEvent, id: ElementId) {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setSelected(id);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const l = layoutRef.current;
    if (pointers.current.size === 1) {
      const { fx, fy } = frac(e);
      const pos = id === 'qr' ? l.qr : l.logo;
      drag.current = { id, fx, fy, x: pos.x, y: pos.y };
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { id, dist: Math.hypot(a!.x - b!.x, a!.y - b!.y) || 1, size: sizeOf(id, l) };
      drag.current = null;
    }
  }

  function onMove(e: RPointerEvent, id: ElementId) {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current && pinch.current.id === id && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      const [lo, hi] = limits(id);
      patch(id, { size: clamp(pinch.current.size * (dist / pinch.current.dist), lo, hi) });
    } else if (drag.current && drag.current.id === id) {
      const { fx, fy } = frac(e);
      moveTo(id, drag.current.x + (fx - drag.current.fx), drag.current.y + (fy - drag.current.fy));
    }
  }

  function onUp(e: RPointerEvent) {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) { drag.current = null; setGuides({ x: false, y: false }); }
    else if (pointers.current.size === 1 && drag.current === null) {
      // one finger left after a pinch: continue dragging from here
      const [only] = [...pointers.current.values()];
      const id = selected;
      if (id && only) {
        const { fx, fy } = frac({ clientX: only.x, clientY: only.y });
        const pos = id === 'qr' ? layoutRef.current.qr : layoutRef.current.logo;
        drag.current = { id, fx, fy, x: pos.x, y: pos.y };
      }
    }
  }

  function onHandleDown(e: RPointerEvent, id: ElementId) {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    setSelected(id);
    resizing.current = id;
  }

  function onHandleMove(e: RPointerEvent, id: ElementId) {
    if (resizing.current !== id) return;
    const { fx } = frac(e);
    const l = layoutRef.current;
    const cx = id === 'qr' ? l.qr.x : l.logo.x;
    const [lo, hi] = limits(id);
    const size = clamp(2 * (fx - cx), lo, hi);
    // keep the grown element inside the wallpaper
    const bw = size;
    const bh = id === 'qr' ? (size * width) / height : (size * width * (logoRatio ?? 0.3)) / height;
    const cy = id === 'qr' ? l.qr.y : l.logo.y;
    patch(id, {
      size,
      x: clamp(cx, Math.min(bw / 2, 0.5), Math.max(1 - bw / 2, 0.5)),
      y: clamp(cy, Math.min(bh / 2, 0.5), Math.max(1 - bh / 2, 0.5)),
    });
  }

  function onHandleUp() { resizing.current = null; }

  function onKey(e: RKeyboardEvent, id: ElementId) {
    const step = e.shiftKey ? 0.05 : 0.01;
    const l = layoutRef.current;
    const pos = id === 'qr' ? l.qr : l.logo;
    const [lo, hi] = limits(id);
    const sz = sizeOf(id, l);
    switch (e.key) {
      case 'ArrowLeft': moveTo(id, pos.x - step, pos.y); break;
      case 'ArrowRight': moveTo(id, pos.x + step, pos.y); break;
      case 'ArrowUp': moveTo(id, pos.x, pos.y - step); break;
      case 'ArrowDown': moveTo(id, pos.x, pos.y + step); break;
      case '+': case '=': patch(id, { size: clamp(sz + step, lo, hi) }); break;
      case '-': patch(id, { size: clamp(sz - step, lo, hi) }); break;
      default: return;
    }
    e.preventDefault();
  }

  const ids: ElementId[] = logoRatio ? ['qr', 'logo'] : ['qr'];
  const labels: Record<ElementId, string> = {
    qr: t('qrWallpaper.qrCode', 'QR-Code'),
    logo: t('qrWallpaper.logo', 'Logo'),
  };

  return (
    <div
      ref={wrapRef}
      className="relative w-full select-none [--h:40vh] md:[--h:60vh]"
      style={{ aspectRatio: `${width} / ${height}`, maxWidth: `calc(var(--h) * ${width / height})` }}
      onPointerDown={(e) => { if (e.target === e.currentTarget) setSelected(null); }}
    >
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={t('qrWallpaper.preview', 'Vorschau')}
        className="absolute inset-0 h-full w-full rounded-[22px] border border-line shadow-lg"
        onPointerDown={() => setSelected(null)}
      />

      {showTemplate && (
        <svg viewBox={`0 0 ${width} ${height}`} className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
          <g fill="rgba(120,120,130,0.35)" stroke="rgba(120,120,130,0.55)" strokeWidth={Math.max(2, width / 400)} strokeDasharray={`${width / 60} ${width / 80}`}>
            <rect x={width * 0.3} y={height * 0.07} width={width * 0.4} height={height * 0.03} rx={width * 0.015} />
            <rect x={width * 0.2} y={height * 0.105} width={width * 0.6} height={height * 0.135} rx={width * 0.04} />
            <circle cx={width * 0.15} cy={height * 0.925} r={width * 0.075} />
            <circle cx={width * 0.85} cy={height * 0.925} r={width * 0.075} />
          </g>
          <text x={width / 2} y={height * 0.2} textAnchor="middle" fontSize={height * 0.085} fontWeight="700" fill="rgba(120,120,130,0.55)" fontFamily="system-ui, sans-serif">9:41</text>
        </svg>
      )}

      {(guides.x || guides.y) && (
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          {guides.x && <div className="absolute inset-y-0 left-1/2 border-l border-dashed border-accent-500" />}
          {guides.y && <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-accent-500" />}
        </div>
      )}

      {ids.map((id) => {
        const pos = id === 'qr' ? layout.qr : layout.logo;
        const b = box(id);
        const isSel = selected === id;
        return (
          <div
            key={id}
            role="button"
            tabIndex={0}
            aria-label={t('qrWallpaper.moveHint', '{{name}}: mit Pfeiltasten verschieben, mit + und − vergrößern', { name: labels[id] })}
            onPointerDown={(e) => onDown(e, id)}
            onPointerMove={(e) => onMove(e, id)}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            onKeyDown={(e) => onKey(e, id)}
            onFocus={() => setSelected(id)}
            className={`absolute cursor-move touch-none rounded-sm outline-none ${isSel ? 'ring-2 ring-accent-500' : 'ring-1 ring-transparent hover:ring-accent-400/70 focus-visible:ring-2 focus-visible:ring-accent-500'}`}
            style={{
              left: `${(pos.x - b.w / 2) * 100}%`,
              top: `${(pos.y - b.h / 2) * 100}%`,
              width: `${b.w * 100}%`,
              height: `${b.h * 100}%`,
            }}
          >
            {isSel && (
              <span
                onPointerDown={(e) => onHandleDown(e, id)}
                onPointerMove={(e) => onHandleMove(e, id)}
                onPointerUp={onHandleUp}
                onPointerCancel={onHandleUp}
                aria-hidden="true"
                className="absolute -bottom-2.5 -right-2.5 h-5 w-5 cursor-nwse-resize touch-none rounded-full border-2 border-white bg-accent-600 shadow before:absolute before:-inset-3 before:content-['']"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
